import {
  VIOLATION_AGENCIES,
  VIOLATION_AGENCY_LABELS,
  type ViolationAgency,
} from '@sitewise/shared';
import { EmptyState, ErrorState, Skeleton, cn } from '@sitewise/ui';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Building2, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { fetchViolationDashboard } from '../../../api/violations.js';

/**
 * `/violations` — agency board. One card per monitoring agency with
 * open-violation totals. Drill into a card for the building breakdown.
 */
export function ViolationsBoardPage() {
  const dashQuery = useQuery({
    queryKey: ['violations', 'dashboard'],
    queryFn: fetchViolationDashboard,
  });

  const counts = new Map<ViolationAgency, number>(
    (dashQuery.data?.openByAgency ?? []).map((r) => [r.agency, r.count]),
  );
  const totalOpen = VIOLATION_AGENCIES.reduce((sum, a) => sum + (counts.get(a) ?? 0), 0);

  return (
    <div className="space-y-6 p-4">
      <header>
        <h1 className="text-xl font-semibold">Violations</h1>
        <p className="text-sm text-fg-muted">
          Open agency violations and complaints by source.
          {dashQuery.data ? (
            <>
              {' '}
              <span className="tabular-nums text-fg-secondary">{totalOpen}</span> open total.
            </>
          ) : null}
        </p>
      </header>

      {dashQuery.isError ? (
        <ErrorState description="Could not load violation board." />
      ) : dashQuery.isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-28 w-full rounded-lg" />
          ))}
        </div>
      ) : (
        <>
          {totalOpen === 0 ? (
            <EmptyState
              icon={AlertTriangle}
              title="No open violations"
              description="When agency monitoring sources detect a new violation for one of your buildings, totals will show on these agency boards."
            />
          ) : null}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {VIOLATION_AGENCIES.map((agency) => {
              const count = counts.get(agency) ?? 0;
              return (
                <Link
                  key={agency}
                  to={`/violations/agency/${agency}`}
                  className={cn(
                    'group flex flex-col justify-between rounded-lg border border-border bg-surface p-4 transition-colors',
                    count > 0
                      ? 'hover:border-fg-muted hover:bg-surface-muted'
                      : 'opacity-60 hover:opacity-100',
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-xs font-medium uppercase tracking-wide text-fg-muted">
                        Agency
                      </div>
                      <div className="mt-1 text-lg font-semibold tracking-tight">
                        {VIOLATION_AGENCY_LABELS[agency]}
                      </div>
                    </div>
                    <ChevronRight className="mt-1 h-4 w-4 text-fg-muted transition-transform group-hover:translate-x-0.5" />
                  </div>
                  <div className="mt-4 flex items-end justify-between">
                    <div>
                      <div className="text-3xl font-semibold tabular-nums leading-none">
                        {count}
                      </div>
                      <div className="mt-1 text-xs text-fg-muted">
                        {count === 1 ? 'open violation' : 'open violations'}
                      </div>
                    </div>
                    <Building2 className="h-4 w-4 text-fg-muted" />
                  </div>
                </Link>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
